import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { CompanyChip, Notice, PipelineMini } from "../../design/components";
import { INBOUND_STAGES, INBOUND_STAGE_LABEL, inboundStageIndex } from "../../domain/stages";
import { lazyList } from "../../i18n/localized";
import { dateTime, omr } from "../../lib/format";
import type { InboundDocumentRow } from "../../api/inbound";
import styles from "./inbound.module.css";

/** Lazy: the labels follow the current language (see i18n/localized.ts). */
export const INBOUND_STAGE_STRIP = lazyList(INBOUND_STAGES.length, (i) => INBOUND_STAGE_LABEL[INBOUND_STAGES[i]!]);

export function InboundStageCell({ stage }: { stage: string }) {
  const index = inboundStageIndex(stage);
  // An unknown stage lights no pip rather than claiming the first one.
  if (index < 0) return <span className={styles.stageText}>{stage}</span>;
  return (
    <span className="row-tight">
      <PipelineMini index={index} stages={INBOUND_STAGE_STRIP} />
      <span className={styles.stageText}>{INBOUND_STAGE_STRIP[index]}</span>
    </span>
  );
}

export function InFlightTable({ rows, isLoading }: { rows: InboundDocumentRow[]; isLoading: boolean }) {
  const { t } = useTranslation();
  if (!rows.length && !isLoading) {
    return (
      <div className="card-body">
        <Notice kind="info">{t("inbound.inFlight.empty", "Nothing in flight.")}</Notice>
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="tbl" aria-label={t("inbound.inFlight.tableLabel", "In flight")}>
        <thead>
          <tr>
            <th className="nowrap">{t("inbound.inFlight.colReceived", "Received")}</th>
            <th>{t("inbound.inFlight.colSupplier", "Supplier")}</th>
            <th>{t("inbound.inFlight.colInvoiceNumber", "Invoice number")}</th>
            <th>{t("inbound.inFlight.colCompany", "Company")}</th>
            <th className="r">{t("inbound.inFlight.colTotal", "Total")}</th>
            <th>{t("inbound.inFlight.colStage", "Stage")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="nowrap">{dateTime(row.createdAt)}</td>
              <td>{row.counterparty ?? "—"}</td>
              <td>
                <Link className="link" to={`/inbound/${row.id}`}>
                  {row.invoiceNumber ?? row.id}
                </Link>
              </td>
              <td>
                {row.entityId ? (
                  <CompanyChip entityId={row.entityId} name={row.entityName ?? ""} code={row.entityCode ?? ""} />
                ) : (
                  "—"
                )}
              </td>
              <td className="r nowrap">
                {omr(row.totalAmount)}
                {row.currency ? ` ${row.currency}` : ""}
              </td>
              <td className="nowrap">
                <InboundStageCell stage={row.stage} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
